import React, {
  useRef,
  useEffect,
  forwardRef,
  useImperativeHandle,
  useMemo,
} from "react";
import { Container } from "@pixi/react";
import { useSelector } from "react-redux";
import { useDimensions } from "../../../Contexts/DimensionsContext";
import HoldAndWinCollectorDisplayController from "./HoldAndWinCollectorDisplayController";
import { safeInit } from "../../../utilities";
import { selectHoldAndWinActive } from "../../../State/selectors";

/**
 * HoldAndWinCollectorDisplay - Displays collector spine animation
 * Shows when collection starts (replaces counter)
 */
const HoldAndWinCollectorDisplay = forwardRef((props, ref) => {
  const holdAndWinActive = useSelector(selectHoldAndWinActive);

  const collectorDisplayControllerRef = useRef(null);
  const collectorDisplayContainerRef = useRef(null);

  const dimensionsContext = useDimensions();
  const { dimensions, symbolHeight, device, orientation } = dimensionsContext;

  // Get device-specific Y offset
  const yOffset = useMemo(() => {
    const config = HoldAndWinCollectorDisplayController.getConfigForDevice(
      device,
      orientation
    );
    return symbolHeight * config.yOffsetMultiplier;
  }, [device, orientation, symbolHeight]);

  // Initialize controller when container is ready and Hold and Win becomes active
  useEffect(() => {
    if (
      collectorDisplayContainerRef.current &&
      !collectorDisplayControllerRef.current &&
      holdAndWinActive
    ) {
      safeInit(() => {
        collectorDisplayControllerRef.current =
          new HoldAndWinCollectorDisplayController(
            collectorDisplayContainerRef.current,
            dimensions,
            symbolHeight,
            device,
            orientation
          );

        // Don't show collector here - let the showHoldAndWinCollector effect handle it
      });
    }
  }, [holdAndWinActive, dimensions, symbolHeight, device, orientation]);

  // Handle dimension changes
  useEffect(() => {
    if (collectorDisplayControllerRef.current && symbolHeight && dimensions) {
      collectorDisplayControllerRef.current.updateDimensions(
        dimensions,
        symbolHeight,
        device,
        orientation
      );
    }
  }, [symbolHeight, dimensions, device, orientation]);

  // NOTE: We don't auto-show/hide collector based on showHoldAndWinCollector state
  // The collector is shown/hidden manually via imperative methods from the collection controller
  // showHoldAndWinCollector is only used to hide the counter component

  // Expose imperative methods to parent via ref
  useImperativeHandle(ref, () => ({
    getController: () => collectorDisplayControllerRef.current,

    showCollector: () => {
      collectorDisplayControllerRef.current?.showCollector();
    },

    hideCollector: () => {
      collectorDisplayControllerRef.current?.hideCollector();
    },

    updateCollectorValue: (value) => {
      collectorDisplayControllerRef.current?.updateCollectorValue(value);
    },

    enableSkipMode: () => {
      collectorDisplayControllerRef.current?.enableSkipMode();
    },

    disableSkipMode: () => {
      collectorDisplayControllerRef.current?.disableSkipMode();
    },

    // Counter animation methods
    setMaxWinCheck: (threshold, callback) => {
      collectorDisplayControllerRef.current?.setMaxWinCheck(threshold, callback);
    },

    startCounterAnimation: (toValue, duration, onComplete) => {
      collectorDisplayControllerRef.current?.startCounterAnimation(toValue, duration, onComplete);
    },

    isCounterAnimating: () => {
      return collectorDisplayControllerRef.current?.isCounterAnimating() || false;
    },

    stopCounterAnimation: () => {
      collectorDisplayControllerRef.current?.stopCounterAnimation();
    },
  }));

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (collectorDisplayControllerRef.current) {
        collectorDisplayControllerRef.current.cleanup();
      }
    };
  }, []);

  if (!dimensions || !dimensions.width || !dimensions.height) {
    return null;
  }

  return (
    <Container
      name="hold_and_win_collector_display"
      x={0}
      y={yOffset}
      sortableChildren={true}
      zIndex={8}
      ref={collectorDisplayContainerRef}
      visible={holdAndWinActive}
    />
  );
});

export default HoldAndWinCollectorDisplay;
